"use client";

import { ReactNode, useEffect, useRef } from "react";

/**
 * TurnstileProvider: Initializes Cloudflare Turnstile widget
 * The widget is invisible and managed directly via window.turnstile
 * No context needed - API calls fetch tokens directly
 */
export function TurnstileProvider({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initTurnstile = () => {
      // @ts-expect-error - window.turnstile is injected by Cloudflare
      if (!window.turnstile) {
        // Retry if Turnstile hasn't loaded yet
        setTimeout(initTurnstile, 300);
        return;
      }

      // Ensure the DOM element exists
      const element = rootRef.current || document.getElementById("turnstile-root");
      if (!element) {
        console.warn("Turnstile root element not found, retrying...");
        setTimeout(initTurnstile, 100);
        return;
      }

      try {
        // Clear any existing widgets in the element
        element.innerHTML = "";

        // @ts-expect-error - window.turnstile.render is from Cloudflare SDK
        const widgetId = window.turnstile.render(element, {
          sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
          size: "invisible",
          // Callback function called when challenge is completed
          callback: (token: string) => {
            // Store token in window for retrieval
            // @ts-expect-error - custom property for token storage
            window.turnstileToken = token;
          },
          // Called when challenge expires
          "expired-callback": () => {
            // @ts-expect-error - custom property for token storage
            window.turnstileToken = null;
          },
          // Called on error
          "error-callback": () => {
            // @ts-expect-error - custom property for token storage
            window.turnstileToken = null;
          },
        });
        
        // Store widget ID globally for execute calls
        // @ts-expect-error - custom property for widget ID storage
        window.turnstileWidgetId = widgetId;
      } catch (error) {
        console.error("Error initializing Turnstile:", error);
        // Retry if there was an error
        setTimeout(initTurnstile, 300);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const animationFrameId = requestAnimationFrame(() => {
      initTurnstile();
    });

    // Cleanup: Remove widget on unmount
    return () => {
      cancelAnimationFrame(animationFrameId);
      try {
        // @ts-expect-error - window.turnstile.remove is from Cloudflare SDK
        window.turnstile?.remove?.();
      } catch (error) {
        console.error("Error removing Turnstile:", error);
      }
    };
  }, []);

  return (
    <>
      {children}
      <div id="turnstile-root" ref={rootRef} />
    </>
  );
}